import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Interface/Button/Button";
import { EsimQrCode } from "@/components/Portal/components/EsimQrCode/EsimQrCode.tsx";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import { useIsMobile } from "@/hooks/useIsMobile";
import { SimCardDetail } from "@/services/model";
import { parseEsimMatchingId } from "@/utils/esimActivationCode";
import {
  getEsimInstallUrl,
  tryNavigateToEsimInstall,
} from "@/utils/esimInstallUrl";

import { RegistrationDetails } from "./RegistrationDetails/RegistrationDetails";

export const UnscannedQRCodeDetails = ({
  simDetail,
}: {
  simDetail: SimCardDetail;
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: "cockpit.managementTile.details.sections.details",
  });
  const { t: t2 } = useTranslation();
  const isMobile = useIsMobile();
  const [showQrCode, setShowQrCode] = useState(false);
  const [showRegistrationDetails, setShowRegistrationDetails] = useState(false);

  if (!simDetail.matchingId) {
    return (
      <div data-testid="activate-esim">
        <SubscriptionInfoBox>
          <p className="text-body-xs text-foreground-secondary">
            {t("registrationDetails.missingMatchingId")}
          </p>
        </SubscriptionInfoBox>
      </div>
    );
  }

  // Phones get a native-install CTA; tablets/desktop (and phones without
  // an install URL) show the QR so another device can scan it.
  const lpa = parseEsimMatchingId(simDetail.matchingId)?.completeActivationCode;
  const canNativeInstall = !!lpa && !!getEsimInstallUrl(lpa);
  const showQr = !isMobile || showQrCode || !canNativeInstall;

  return (
    <div data-testid="activate-esim" className="flex flex-col gap-2">
      {showQr ? (
        <SubscriptionInfoBox className="flex flex-row items-start gap-2">
          <div className="border-primary flex items-start rounded-md border p-2">
            <EsimQrCode matchingId={simDetail.matchingId} size={72} />
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-body-sm font-semibold leading-none text-foreground-primary">
              {t("scanQRCode")}
            </p>
            <p className="text-body-xs leading-none text-foreground-secondary">
              {t("scanQRCodeText")}
            </p>
            <p>
              <a
                className="text-body-xs font-semibold text-foreground-primary underline"
                href={t2("layout.tiles.qrCodeTile.instructionsLink")}
                target="_blank"
                rel="noreferrer"
              >
                {t("instructionsLinkText")}
              </a>
            </p>
          </div>
        </SubscriptionInfoBox>
      ) : (
        <SubscriptionInfoBox className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <p className="text-body-sm font-semibold leading-none text-foreground-primary">
              {t("installOnThisDevice.title")}
            </p>
            <p className="text-body-sm leading-none text-foreground-secondary">
              {t("installOnThisDevice.text")}
            </p>
          </div>
          <Button
            dataTestid="install-esim-button"
            variant="primary"
            fullWidth
            onClick={() => {
              if (!tryNavigateToEsimInstall(simDetail.matchingId)) {
                setShowQrCode(true);
              }
            }}
          >
            {t("installOnThisDevice.button")}
          </Button>
        </SubscriptionInfoBox>
      )}
      <SubscriptionInfoBox className="flex flex-col gap-3">
        {showRegistrationDetails && (
          <RegistrationDetails matchingId={simDetail.matchingId} />
        )}
        <Button
          dataTestid="show-code-button"
          size="sm"
          variant="secondary"
          aria-expanded={showRegistrationDetails}
          // Only advertise the id once RegistrationDetails is actually
          // mounted above - it doesn't exist in the DOM while collapsed.
          aria-controls={
            showRegistrationDetails ? "registration-details" : undefined
          }
          onClick={() => setShowRegistrationDetails(!showRegistrationDetails)}
        >
          {showRegistrationDetails
            ? t("registrationDetails.hideCode")
            : t("registrationDetails.showCode")}
        </Button>
      </SubscriptionInfoBox>
    </div>
  );
};
